import type { ReactElement } from "react";
import { useSearchParams } from "react-router";

import { FormSummary } from "../../forms/fields.js";
import { useApiForm } from "../../forms/use-api-form.js";

import { tenantIdOf } from "./center-back-link.js";
import type { CenterChargeImport, CenterChargeImportLast } from "./charge-types.js";
import { adminCenterApi } from "./urls.js";

/**
 * 「今すぐ取り込む」の前に聞く文（重要な操作は最後にダイアログで確かめる。docs/01 §7）。
 * <b>PAY.JP には書かない</b>（読むだけ）ことと、時間がかかることを伝える。
 */
export const chargesImportDialog = (): string =>
    "PAY.JP から課金の記録を取り込みます。PAY.JP の側の課金や契約は変わりません。数分かかります。よろしいですか？";

/** 最後の取り込みの状態（札・自動か手動か・時刻・範囲・件数か理由）。 */
function LastImport({ last }: { readonly last: CenterChargeImportLast | null }): ReactElement {
    if (last === null) {
        return <p className="text-sm text-slate-500">まだ 1 度も取り込んでいません。</p>;
    }
    return (
        <div className="text-sm" data-charge-import-last="">
            <p>
                <span className={last.stateClass}>{last.stateLabel}</span>{" "}
                <span className="text-slate-500">{`${last.triggerLabel}・${last.timeLabel}`}</span>
            </p>
            {last.rangeLabel === null ? null : <p className="mt-1 text-xs text-slate-500">{`範囲: ${last.rangeLabel}`}</p>}
            {last.detail === null ? null : <p className="mt-1 text-xs text-slate-500">{last.detail}</p>}
        </div>
    );
}

/**
 * PAY.JP からの取り込み（最後の状態・自動の間隔・「今すぐ取り込む」）。
 * <b>押すと受け付けるだけ</b>で、取り込みは常駐処理が後ろで動かす（数分）。終わったかは画面を開き直すと分かる。
 * <b>押せないのは取り込みの最中だけ</b>（<c>last.busy</c>。自動の取り込みを止めてあっても手動では取り込める）。
 * 押せるかの正本はサーバで、断り（取り込み中など）はフォームの先頭に出す。
 */
export function ChargeImportSection({ state }: { readonly state: CenterChargeImport }): ReactElement {
    const [search] = useSearchParams();
    const tenantId = tenantIdOf(search);
    const form = useApiForm(adminCenterApi.chargesImport, undefined, chargesImportDialog());
    const busy = state.last?.busy ?? false;

    return (
        <section className="card mt-6 mb-4 p-6" data-charge-import="">
            <h2 className="section-title">PAY.JP からの取り込み</h2>
            <LastImport last={state.last} />
            <p className="mt-2 text-xs text-slate-500" data-charge-import-schedule="">
                {state.scheduled
                    ? `自動の取り込み: ${state.intervalLabel}`
                    : "自動の取り込みは止めてあります（手動でだけ取り込みます）。"}
            </p>
            <form method="post" className="mt-3" onSubmit={form.onSubmit}>
                <FormSummary messages={form.summary} />
                {/* 戻り先に載せるだけ（終わるとこの画面の同じ契約の絞り込みへ戻る） */}
                {tenantId === null ? null : <input type="hidden" name="tenantId" value={String(tenantId)} />}
                <div className="flex flex-wrap items-center gap-3">
                    <button type="submit" className="btn-secondary btn-sm" disabled={busy || form.busy}>今すぐ取り込む</button>
                    {busy
                        ? <span className="text-xs text-slate-500">取り込み中です。終わってから押せます。</span>
                        : <span className="text-xs text-slate-500">PAY.JP の記録を読むだけで、PAY.JP の側は変えません。</span>}
                </div>
            </form>
        </section>
    );
}
